// Comprehensive ResizeObserver error suppression
export const suppressResizeObserverErrors = () => {
  // Store original ResizeObserver
  const OriginalResizeObserver = window.ResizeObserver;
  
  // Helper function to check if an error is ResizeObserver related
  const isResizeObserverError = (message: any): boolean => {
    if (!message) return false;
    const messageStr = String(message).toLowerCase();
    return messageStr.includes('resizeobserver') && 
           (messageStr.includes('loop') || messageStr.includes('undelivered'));
  };
  
  // Override ResizeObserver to catch and suppress errors
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      const wrappedCallback: ResizeObserverCallback = (entries, observer) => {
        try {
          callback(entries, observer);
        } catch (error) {
          // Suppress ResizeObserver errors
          if (error instanceof Error && isResizeObserverError(error.message)) {
            console.debug('Suppressed ResizeObserver error:', error.message);
            return;
          }
          throw error;
        }
      };
      
      super(wrappedCallback);
    }
  };

  // Additional error handlers with improved detection
  const handleError = (event: ErrorEvent) => {
    if (isResizeObserverError(event.message) || isResizeObserverError(event.error?.message)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (isResizeObserverError(event.reason?.message) || isResizeObserverError(String(event.reason))) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  };

  // Override the window error handler more comprehensively
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (isResizeObserverError(String(message))) {
      return true; // Prevent default handling
    }
    return originalOnError ? originalOnError(message, source, lineno, colno, error) : false;
  };

  // Install global error handlers with both capture and bubble phases
  window.addEventListener('error', handleError, true);
  window.addEventListener('error', handleError, false);
  window.addEventListener('unhandledrejection', handleUnhandledRejection, true);
  window.addEventListener('unhandledrejection', handleUnhandledRejection, false);
  document.addEventListener('error', handleError, true);
  document.addEventListener('error', handleError, false);

  // Cleanup function
  return () => {
    window.ResizeObserver = OriginalResizeObserver;
    window.onerror = originalOnError;
    window.removeEventListener('error', handleError, true);
    window.removeEventListener('error', handleError, false);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection, false);
    document.removeEventListener('error', handleError, true);
    document.removeEventListener('error', handleError, false);
  };
}; 